import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

/** Admin > AI & Credits: global AI switch, free job-seeker allowance, per-feature credit cost, 30-day usage. */
export default function AiCreditsPanel() {
  const { api } = useAuth();
  const [settings, setSettings] = useState(null);
  const [usage, setUsage] = useState({});
  const [saving, setSaving] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [providerForm, setProviderForm] = useState({ model: '', base_url: '' });

  const load = useCallback(async () => {
    try {
      const [s, u] = await Promise.all([api.get('/admin/ai-settings'), api.get('/admin/ai-usage?days=30')]);
      setSettings(s.data);
      setProviderForm({ model: s.data.provider?.model || '', base_url: s.data.provider?.base_url || '' });
      setUsage(u.data.by_feature || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load AI settings');
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const save = async (extraProvider = {}) => {
    setSaving(true);
    try {
      const provider = { ...providerForm, ...extraProvider };
      if (apiKey.trim()) provider.api_key = apiKey.trim();
      const res = await api.put('/admin/ai-settings', {
        provider,
        enabled: settings.enabled,
        seeker_monthly_credits: parseInt(settings.seeker_monthly_credits, 10),
        costs: Object.fromEntries(Object.entries(settings.costs).map(([k, v]) => [k, parseInt(v, 10) || 0])),
      });
      setSettings(prev => ({ ...prev, ...res.data }));
      setProviderForm({ model: res.data.provider?.model || '', base_url: res.data.provider?.base_url || '' });
      setApiKey('');
      toast.success('AI settings saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save AI settings');
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div className="flex justify-center p-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="ai-credits-panel">
      <Card>
        <CardHeader>
          <CardTitle>AI features</CardTitle>
          <CardDescription>
            Provider status:{' '}
            {settings.provider_configured
              ? <Badge className="bg-emerald-100 text-emerald-700">API key configured</Badge>
              : <Badge className="bg-red-100 text-red-700">No API key — add one below; AI endpoints return 503 until then</Badge>}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border rounded-lg p-4 space-y-3" data-testid="ai-provider-box">
            <p className="text-sm font-semibold">OpenAI API key</p>
            <p className="text-xs text-muted-foreground">
              {settings.provider?.key_source === 'admin' && <>Saved key in use ({settings.provider.key_hint}). </>}
              {settings.provider?.key_source === 'environment' && <>Currently using the key from the server environment. A key saved here overrides it. </>}
              Paste a new key to replace it. The saved key is never shown again.
            </p>
            <div className="grid gap-3 md:grid-cols-3">
              <div className="space-y-1 md:col-span-3">
                <Label>API key</Label>
                <Input type="password" autoComplete="new-password" placeholder="sk-..." value={apiKey}
                  onChange={e => setApiKey(e.target.value)} data-testid="ai-api-key" />
              </div>
              <div className="space-y-1">
                <Label>Model (optional)</Label>
                <Input placeholder="gpt-4o-mini" value={providerForm.model}
                  onChange={e => setProviderForm({ ...providerForm, model: e.target.value })} data-testid="ai-model" />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label>Base URL (optional, for OpenAI-compatible gateways)</Label>
                <Input placeholder="https://api.openai.com/v1" value={providerForm.base_url}
                  onChange={e => setProviderForm({ ...providerForm, base_url: e.target.value })} data-testid="ai-base-url" />
              </div>
            </div>
            {settings.provider?.key_source === 'admin' && (
              <Button type="button" variant="outline" size="sm" className="text-red-600" disabled={saving}
                onClick={() => { if (window.confirm('Remove the saved API key?')) save({ clear_api_key: true }); }}>
                Remove saved key
              </Button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={settings.enabled} onCheckedChange={v => setSettings({ ...settings, enabled: v })} data-testid="ai-enabled" />
            <Label>AI features enabled platform-wide</Label>
          </div>
          <div className="max-w-xs space-y-1">
            <Label>Free monthly credits for job seekers / students (-1 = unlimited)</Label>
            <Input type="number" min={-1} value={settings.seeker_monthly_credits}
              onChange={e => setSettings({ ...settings, seeker_monthly_credits: e.target.value })} data-testid="ai-seeker-credits" />
          </div>
          <div>
            <p className="text-sm font-semibold mb-2">Credits charged per use</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(settings.features || []).map(f => (
                <div key={f.key} className="flex items-center gap-3 border rounded-lg p-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{f.label}</p>
                    <p className="text-xs text-muted-foreground">
                      {f.audience} · last 30 days: {usage[f.key]?.calls || 0} calls / {usage[f.key]?.credits || 0} credits
                    </p>
                  </div>
                  <Input type="number" min={0} className="w-20" value={settings.costs[f.key] ?? f.default_cost}
                    onChange={e => setSettings({ ...settings, costs: { ...settings.costs, [f.key]: e.target.value } })}
                    data-testid={`ai-cost-${f.key}`} />
                </div>
              ))}
            </div>
          </div>
          <Button onClick={() => save()} disabled={saving} className="bg-[#003366] hover:bg-[#003366]/90" data-testid="ai-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Save AI settings
          </Button>
          <p className="text-xs text-muted-foreground">
            Employer credit allowances and which AI features each plan includes are set per plan in the Plans tab.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
